import { z } from 'zod';

/**
 * Environment validation (01-architecture.md §8, 06-realtime.md §2).
 * - API/auth base URLs are required for the app to function at all.
 * - Reverb values are required before the realtime feature connects (V1-05);
 *   they are validated here but only reported, so a missing key fails clearly
 *   at the point of use instead of white-screening the whole console.
 * - Problems report VARIABLE NAMES ONLY — never values (no secrets in logs).
 */
const requiredUrl = z.string().url();

export interface EnvReport {
  ok: boolean;
  /** Names of required variables that are missing/invalid. */
  missingRequired: string[];
  /** Names of realtime variables not yet configured (needed by V1-05). */
  missingRealtime: string[];
}

export interface AppEnv {
  apiBaseUrl: string;
  authBaseUrl: string;
  /** Base a stored path is appended to, prefix included; '' disables image display. */
  mediaBaseUrl: string;
  reverb: {
    appKey: string;
    host: string;
    port: number;
    scheme: 'https' | 'http';
  } | null;
}

function readEnv(): { env: AppEnv; report: EnvReport } {
  const raw = import.meta.env;
  const missingRequired: string[] = [];
  const missingRealtime: string[] = [];

  const api = requiredUrl.safeParse(raw.VITE_API_BASE_URL);
  if (!api.success) missingRequired.push('VITE_API_BASE_URL');
  const auth = requiredUrl.safeParse(raw.VITE_AUTH_BASE_URL);
  if (!auth.success) missingRequired.push('VITE_AUTH_BASE_URL');

  const reverbKey = raw.VITE_REVERB_APP_KEY;
  const reverbHost = raw.VITE_REVERB_HOST;
  if (!reverbKey) missingRealtime.push('VITE_REVERB_APP_KEY');
  if (!reverbHost) missingRealtime.push('VITE_REVERB_HOST');

  const media = z.string().url().safeParse(raw.VITE_MEDIA_BASE_URL);

  const env: AppEnv = {
    apiBaseUrl: api.success ? api.data : '',
    authBaseUrl: auth.success ? auth.data : '',
    mediaBaseUrl: media.success ? media.data.replace(/\/+$/, '') : '',
    reverb:
      reverbKey && reverbHost
        ? {
            appKey: reverbKey,
            host: reverbHost,
            port: Number(raw.VITE_REVERB_PORT ?? 443),
            scheme: raw.VITE_REVERB_SCHEME === 'http' ? 'http' : 'https',
          }
        : null,
  };

  return {
    env,
    report: {
      ok: missingRequired.length === 0,
      missingRequired,
      missingRealtime,
    },
  };
}

const result = readEnv();

export const env: AppEnv = result.env;
export const envReport: EnvReport = result.report;

if (!envReport.ok && import.meta.env.DEV) {
  // Names only — never values.
  console.error(
    `[sg-console] Missing/invalid required environment variables: ${envReport.missingRequired.join(', ')}. ` +
      'Copy .env.example to .env and fill them in.',
  );
}
